import React, { useContext, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { makeStyles, Theme } from '@material-ui/core';
import { ProjectDevice, GatewayDefinition, Entity, ApiError } from '@moderepo/mode-internal-apis';
import { DialogResponse, DialogResponseCode, DialogType, hideSlideOutPanel, modeUIContext, showDialog, showErrorNotification, showSlideOutPanel,
    showSuccessNotification, SlideOutPanelLocation } from '@moderepo/mode-ui-state';
import { clearProjectDevices, ritzDataContext, selectLoggedInAgent, selectProjectById, selectProjectDeviceById }
    from '@moderepo/mode-data-state-ritz';
import { BaseCompAction, capitalizeAll, getErrorMessage, SlideOutComp } from '@moderepo/mode-app-components';
import { clearHomeDevices, userAppDataContext, removeHomeDevice, clearEntities, clearDeviceKVStores } from '@moderepo/mode-data-state-user-app';
import clsx from 'clsx';
import { AppAPI } from '@moderepo/mode-apis';
import { useGetProjectGatewayCatalog, useRitzTranslation } from '../../utils/customHooks';
import { RouteHelper } from '../routes/RouteHelper';
import { DeviceBasicInfoContainer } from './DeviceBasicInfoContainer';
import { DeviceDevInfoContainer } from './DeviceDevInfoContainer';
import { AddPreprovisionedDeviceContainer } from '../homes/AddPreprovisionedDeviceContainer';
import { isDeviceRegistrationWizardEnabled, isDeviceRegistrationWizardEnabledForDeviceClass } from '../../utils/utils';
import { ProjectTypeHandlersManager } from '../../utils/ritzExtensionUtils';
import { DeviceRegistrationWizardSettings } from '../../utils/ritzUIExtensionUtils';
import { DeviceRegistrationWizardSlideOutCompOptions, SlideOutPanelCompType } from '../routes/defaultProjectConfig';



const useStyle = makeStyles((theme: Theme) => {
    return {
        secondaryAction: {
            '& .icon': {
                color: theme.palette.primary.main,
            },
            '&.remove': {
                '& .icon': {
                    color: theme.palette.secondary.main,
                },
            },
        },
    };
});


interface DeviceInfoPreviewContainerProps {
    readonly projectId: number;
    readonly deviceId: number | undefined;      // undefined mean this DeviceInfoPreview container is use for adding a new device
    readonly gatewaysOnly?: boolean;            // A flag to indicate that only show Gateways. Default is FALSE. Used for add/create device flow

    // These 2 are only available if the user is looking at a user's home device
    readonly context?: {
        readonly userId?: number | undefined;
        readonly homeId?: number | undefined;
    }
}


/**
 * This Component is used for displaying DeviceInfo on the preview panel for preview.
 */
export const DeviceInfoPreviewContainer = (props: DeviceInfoPreviewContainerProps) => {
    const navigate = useNavigate();
    const { projectId } = props;
    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const { state:dataState, dispatch:dataDispatch } = useContext(ritzDataContext);
    const { dispatch:appDataDispatch } = useContext(userAppDataContext);
    const project = selectProjectById(dataState, projectId);
    const loggedInAgent = selectLoggedInAgent(dataState);
    const { trans } = useRitzTranslation(project);
    const selectedDevice = props.deviceId ? selectProjectDeviceById(dataState, props.deviceId) : undefined;
    const isNewDevice = props.deviceId === undefined;
    const routeHelper = RouteHelper.getInstance(navigate);
    const gatewayCatalog = useGetProjectGatewayCatalog(project);
    const classes = useStyle();


    // find the gatewayDefinition for the selected device from the gateway catalog. If we find a gatewayDefinition, that mean the device is a
    // gateway and we should show different UI.
    const isGateway = useMemo(() => {
        if (gatewayCatalog && selectedDevice) {
            return gatewayCatalog.find((gatewayDef: GatewayDefinition) => {
                return gatewayDef.deviceClass === selectedDevice.deviceClass;
            }) !== undefined;
        }

        return false;
    }, [gatewayCatalog, selectedDevice]);



    /**
     * Get the "deviceRegistrationWizard" settings from the project's uiSettings if this feature is enabled
     */
    const deviceRegistrationWizardSettings = useMemo(() => {
        const isEnabled = isDeviceRegistrationWizardEnabled(project, loggedInAgent);
        if (isEnabled && selectedDevice) {
            const projectUIConfig = ProjectTypeHandlersManager.getInstance().getProjectCustomUIHandler(
                project?.supportToolSettings.projectType,
            )?.getCustomUIConfig(loggedInAgent, project);

            if (projectUIConfig?.enabledFeatures?.deviceRegistrationWizard?.deviceClassesSettings.some((settings) => {
                return settings.deviceClass === selectedDevice.deviceClass;
            })) {
                return projectUIConfig?.enabledFeatures?.deviceRegistrationWizard;
            }
        }
        return undefined;
    }, [loggedInAgent, project, selectedDevice]);


    /**
     * Because this container can be used to display ALL type of devices or only Gateways therefore the translations will depend on whether we are
     * displaying all or gatewaysOnly. If we are displaying all type of devices then all the text/labels/messages will use the term "device"
     * for devices. If we are showing only gateways then instead of using the term "device", we will show a more specific term, "gateway".
     * This map DOES NOT include all the text we use on this page, only the text that has the word "device" in it.
     */
    const labels = useMemo((): {
        readonly viewDeviceDetailsButton: string;
        readonly removeDeviceButton: string;
        readonly confirmRemoveDeviceTitleKey: string;
        readonly confirmRemoveDeviceMessageKey: string;
        readonly deviceRemovedSuccessfullyKey: string;
    } => {
        if (isGateway) {
            return {
                viewDeviceDetailsButton      : capitalizeAll(trans('label.view_gateway_details')),
                removeDeviceButton           : capitalizeAll(trans('label.remove_gateway')),
                confirmRemoveDeviceTitleKey  : 'dialog.confirm_remove_home_gateway.title',
                confirmRemoveDeviceMessageKey: 'dialog.confirm_remove_home_gateway.message',
                deviceRemovedSuccessfullyKey : 'notif.generic_item_removed_successfully',
            };
        }

        return {
            viewDeviceDetailsButton      : capitalizeAll(trans('label.view_device_details')),
            removeDeviceButton           : capitalizeAll(trans('label.remove_device')),
            confirmRemoveDeviceTitleKey  : 'dialog.confirm_remove_home_device.title',
            confirmRemoveDeviceMessageKey: 'dialog.confirm_remove_home_device.message',
            deviceRemovedSuccessfullyKey : 'notif.generic_item_removed_successfully',
        };
    }, [isGateway, trans]);



    /**
     * Unselect the selected device and update the URL
     */
    const unselectDevice = useCallback(() => {
        uiDispatch(hideSlideOutPanel());
    }, [uiDispatch]);



    const onViewDeviceDetails = useCallback((deviceId: number) => {
        routeHelper.gotoDevicePage({ projectId, deviceId, userId: props.context?.userId, homeId: props.context?.homeId });
        uiDispatch(hideSlideOutPanel());
    }, [routeHelper, projectId, props.context, uiDispatch]);



    /**
     * The Add device mode is canceled, unselect the currently selected device if there is one
     */
    const onNewDeviceCanceled = useCallback(() => {
        unselectDevice();
    }, [unselectDevice]);



    /**
     * A device is added to a home, just hide the preview panel
     */
    const onNewDeviceAdded = useCallback(() => {
        unselectDevice();

        // Clear the cached list of devices for the specified home because the list should be changed and need to be refetch.
        if (props.context?.homeId) {
            appDataDispatch(clearHomeDevices(props.context.homeId));
        }
    }, [appDataDispatch, props.context, unselectDevice]);



    /**
     * Delete an entity but before we delete the entity, we will ask the user to enter the entity's name to make sure they really
     * wanted to delete it
     */
    const deleteEntity = useCallback((entity: Entity, errorMessage?: string | undefined) => {

        // Confirm before deleting an Entity. Because deleting an entity can be dangerous, we will ask the agent to enter the entity's name
        uiDispatch(showDialog({
            type       : DialogType.INPUT,
            title      : 'dialog.generic_confirm_delete.title',
            titleData  : { object_name: entity.displaySettings.name, object_term: 'terms.entity' },
            message    : 'dialog.confirm_delete_device_entity.message',
            messageData: {
                entity_name: entity.displaySettings.name,
            },
            positiveButton: 'label.delete',
            negativeButton: 'label.cancel',
            input         : {
                type           : 'text',
                name           : 'label.name',
                error          : errorMessage,
                placeholder    : 'input.delete_object_name.hint_text',
                translationData: {
                    object_term : 'terms.entity',
                    object_field: 'label.name',
                },
                required: true,
            },
            onClose: async (response?: DialogResponse): Promise<void> => {
                if (response && response.code === DialogResponseCode.POSITIVE) {
                    const entityName = response.input ? response.input.trim() : undefined;

                    if (!entityName) {
                        // Name is not provided. Call this function again to show the delete entity dialog with the new error
                        deleteEntity(entity, 'error.input.generic.empty');
                    } else if (entityName !== entity.displaySettings.name) {
                        // Name does not match. Call this function again to show the delete entity dialog with the new error
                        deleteEntity(entity, 'error.input.generic.invalid');
                    } else {
                        try {
                            await AppAPI.getInstance().deleteEntity(props.projectId, entity.entityId);

                            uiDispatch(showSuccessNotification('notif.generic_item_deleted_successfully', {
                                term: capitalizeAll(trans('terms.entity')),
                            }));

                            // Clear all the cached entity data to force UI to reload entity list
                            await appDataDispatch(clearEntities(props.projectId));
                        } catch (error) {
                            // Show error using notification
                            uiDispatch(showErrorNotification(getErrorMessage((error as ApiError).code)));

                            // Call this function again to show the delete entity dialog
                            deleteEntity(entity, undefined);
                        }
                    }
                }
            },
        }));
    }, [appDataDispatch, props.projectId, trans, uiDispatch]);


    /**
     * Delete the entity associated with the provided device if there is one.
     */
    const deleteAssociatedEntityInstance = useCallback(async (device: ProjectDevice) => {

        // If there is a "deviceRegistrationWizardSettings" and if the device's deviceClass is part of the "deviceRegistrationWizardSettings"
        // then continue and delete the entity as well if the entity exists
        if (deviceRegistrationWizardSettings
            && isDeviceRegistrationWizardEnabledForDeviceClass(deviceRegistrationWizardSettings, device.deviceClass)) {

            // Check if there is an entity associated with the device. If there is one, we can delete it. The entity should have the same
            // ID as the device
            const entity = await AppAPI.getInstance().getEntityById(props.projectId, device.id.toString());

            if (entity) {
                deleteEntity(entity);
            }
        }
    }, [deviceRegistrationWizardSettings, deleteEntity, props.projectId]);



    /**
     * On Remove device action from the dropdown menu clicked. Will will remove the device from the home
     */
    const onRemoveDevice = useCallback((homeId: number, device: ProjectDevice, errorMessage?: string | undefined) => {
        uiDispatch(showDialog({
            type       : DialogType.INPUT,
            title      : labels.confirmRemoveDeviceTitleKey,
            titleData  : { object_name: device.name || `#${device.id.toString()}`, object_term: 'terms.device' },
            message    : labels.confirmRemoveDeviceMessageKey,
            messageData:
                {
                    field_name : device.name ? 'label.name' : 'label.id',
                    device_name: (device.name ? device.name : `#${device.id}`),
                },
            positiveButton: 'label.remove',
            negativeButton: 'label.cancel',
            input         : {
                type           : 'text',
                name           : device.name ? 'label.name' : 'label.id',
                error          : errorMessage,
                placeholder    : 'input.remove_object_name.hint_text',
                translationData: {
                    object_term : 'terms.device',
                    object_field: device.name ? 'label.name' : 'label.id',
                },
                required: true,
            },
            onClose: async (response?: DialogResponse): Promise<void> => {
                if (response && response.code === DialogResponseCode.POSITIVE) {
                    const input = response.input ? response.input.trim() : undefined;

                    if (!input) {
                        // input is not provided. Call this function again to show the remove device dialog with the new error
                        onRemoveDevice(homeId, device, 'error.input.generic.empty');
                    } else if (device.name && input !== device.name) {
                        // Name does not match. Call this function again to show the remove device dialog with the new error
                        onRemoveDevice(homeId, device, 'error.input.generic.invalid');
                    } else if (!device.name && Number(input) !== device.id) {
                        // If device does not have a name then the user must enter the ID. If ID does not match call this function again
                        // to show the remove device dialog with the new error
                        onRemoveDevice(homeId, device, 'error.input.generic.invalid');
                    } else {
                        appDataDispatch(removeHomeDevice(homeId, device.id)).then(() => {
                            uiDispatch(showSuccessNotification(labels.deviceRemovedSuccessfullyKey, {
                                term: capitalizeAll(trans(isGateway ? 'terms.gateway' : 'terms.device')),
                            }));
                            unselectDevice();

                            // Clear cached devices and the device's KVs
                            appDataDispatch(clearHomeDevices(device.homeId));
                            appDataDispatch(clearDeviceKVStores(device.id));
                            dataDispatch(clearProjectDevices(props.projectId));
                        }).catch(() => {
                            // ignore error
                        });

                        // This device might be associated with an entity, call "deleteAssociatedEntityInstance" to delete the entity if there is one.
                        deleteAssociatedEntityInstance(device);
                    }
                }
            },
        }));
    }, [uiDispatch, labels.confirmRemoveDeviceTitleKey, labels.confirmRemoveDeviceMessageKey, labels.deviceRemovedSuccessfullyKey, appDataDispatch,
        deleteAssociatedEntityInstance, trans, isGateway, unselectDevice, dataDispatch, props.projectId]);


    /**
     * use useMemo to create the the newUserSettings so that this doesn't get created on every re-render
     */
    const newDeviceSettings = useMemo(() => {
        return {
            onNewDeviceAdded,
            onNewDeviceCanceled,
        };
    }, [onNewDeviceAdded, onNewDeviceCanceled]);


    /**
     * On "Device Registration" button clicked, open the slide out panel to show the Device Registration Wizard
     */
    const onOpenDeviceRegistrationWizard = useCallback((device: ProjectDevice, settings: DeviceRegistrationWizardSettings) => {
        const options: DeviceRegistrationWizardSlideOutCompOptions = {
            location : SlideOutPanelLocation.RIGHT,
            compType : SlideOutPanelCompType.DEVICE_REGISTRATION_WIZARD,
            compProps: {
                data: {
                    projectId                       : props.projectId,
                    deviceId                        : device.id,
                    preselectedHomeId               : undefined,
                    deviceRegistrationWizardSettings: settings,
                },
            },
        };
        uiDispatch(showSlideOutPanel(options));
    }, [props.projectId, uiDispatch]);


    const primaryActions = useMemo(() => {
        if (!isNewDevice) {
            const actions: BaseCompAction[] = [{
                label  : labels.viewDeviceDetailsButton,
                onClick: () => {
                    onViewDeviceDetails(props.deviceId as number);
                },
            }];

            // If there is a "deviceRegistrationWizardSettings" AND a device AND the device's deviceClass can be configured with the
            // device registration wizard then add an action to View/Edit Device Registration
            if (
                deviceRegistrationWizardSettings && selectedDevice
                && isDeviceRegistrationWizardEnabledForDeviceClass(deviceRegistrationWizardSettings, selectedDevice.deviceClass)
            ) {
                actions.push({
                    label  : capitalizeAll(trans('device_registration_wizard.action.edit_registration.label')),
                    onClick: () => {
                        onOpenDeviceRegistrationWizard(selectedDevice, deviceRegistrationWizardSettings);
                    },
                });
            }

            return actions;
        }

        return undefined;
    }, [deviceRegistrationWizardSettings, isNewDevice, labels.viewDeviceDetailsButton, onOpenDeviceRegistrationWizard, onViewDeviceDetails,
        props.deviceId, selectedDevice, trans]);


    const secondaryActions = useMemo(() => {
        if (!isNewDevice && selectedDevice && selectedDevice.homeId) {
            const actions: BaseCompAction[] = [{
                className: clsx(classes.secondaryAction, 'remove'),
                icon     : 'delete',
                label    : labels.removeDeviceButton,
                onClick  : () => {
                    onRemoveDevice(props.context?.homeId as number, selectedDevice);
                },
            }];

            return actions;
        }

        return undefined;
    }, [classes.secondaryAction, isNewDevice, labels.removeDeviceButton, onRemoveDevice, props.context, selectedDevice]);


    const slideOutCompContent = useMemo(() => {
        return (
            <>
                {/*
                    If deviceId is provided, we will use the DeviceBasicInfoContainer to display the device's details.
                    If no deviceId is provided, this Preview container is being used to Add device to home. If project's device provision type is
                    On-demand provision, we need to use this component to ask the user to enter new device info to create a new device.
                 */}
                {(props.deviceId || (!props.deviceId && project && project.onDemandProvisioning && props.context?.homeId)) && (
                    <DeviceBasicInfoContainer
                        className="transparent"
                        infoCompProps={{
                            formProps: {
                                className: 'single-column',
                            },
                        }}
                        projectId={projectId}
                        deviceId={props.deviceId}
                        userId={props.context?.userId}
                        homeId={props.context?.homeId}
                        gatewaysOnly={props.gatewaysOnly}
                        newDeviceSettings={
                            isNewDevice ? newDeviceSettings : undefined
                        }
                    />
                )}

                {/*
                    If no deviceId is provided, this Preview container is being used to Add device to home. If project's device provision type is
                    Preprovisioned devices, we need to use this AddPreprovisionedDeviceContainer to ask the user to select a device to add to
                    the home
                 */}
                {(!props.deviceId && project && !project.onDemandProvisioning && props.context?.homeId) && (
                    <AddPreprovisionedDeviceContainer
                        projectId={projectId}
                        homeId={props.context?.homeId}
                        gatewaysOnly={props.gatewaysOnly}
                        onNewDeviceAdded={onNewDeviceAdded}
                        onNewDeviceCanceled={onNewDeviceCanceled}
                    />
                )}

                {/* Only show Device Dev info is the device exist, not for adding device to home */}
                {props.deviceId && (
                    <DeviceDevInfoContainer
                        className="transparent"
                        infoCompProps={{
                            formProps: {
                                className: 'single-column',
                            },
                        }}
                        projectId={projectId}
                        deviceId={props.deviceId}
                        userId={props.context?.userId}
                        homeId={props.context?.homeId}
                    />
                )}
            </>
        );
    }, [isNewDevice, newDeviceSettings, onNewDeviceAdded, onNewDeviceCanceled, project, projectId, props.context, props.deviceId,
        props.gatewaysOnly]);


    return (
        <SlideOutComp
            primaryActions={primaryActions}
            secondaryActions={secondaryActions}
            compContent={slideOutCompContent}
            closable={!isNewDevice}
            contentContainerProps={{
                className: 'slide-out-comp-container',
            }}
        />
    );
};
